import { ScheduledStep, ScheduleStatus } from 'models/production-planning';
import { Hours } from 'models/organization';
import { t } from 'i18next';

export const isSameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const statusLabel = (status: ScheduleStatus): string => {
    if (status === ScheduleStatus.LATE) return t('production-dashboard:STATUS_LATE');
    if (status === ScheduleStatus.EARLY) return t('production-dashboard:STATUS_DONE');
    if (status === ScheduleStatus.PAUSED) return t('production-dashboard:STATUS_PAUSED');
    return '';
};

/**
 * Overdue work drawn in today's column: late steps, and the unfinished steps of a
 * paused order. A pause keeps their hours counted without marking them late (DD-3605).
 */
export const isCarriedToToday = (step: ScheduledStep, today: Date): boolean =>
    step.deadline < today &&
    (step.scheduleStatus === ScheduleStatus.LATE || (!!step.isPaused && step.scheduleStatus !== ScheduleStatus.EARLY));

/**
 * A department's steps for one day: those due that day, plus, for today, the overdue
 * work carried into it. The overview cell and the department day view both count
 * these, so their hours agree (DD-3802).
 */
export const getDepartmentDaySteps = (steps: ScheduledStep[], departmentName: string, date: Date): ScheduledStep[] => {
    const today = getToday();
    const isToday = isSameDay(date, today);
    return steps.filter((step) => {
        if (step.department !== departmentName) return false;
        if (isSameDay(step.deadline, date)) return true;
        return isToday && isCarriedToToday(step, today);
    });
};

// --- Table utilities ---

export type CellCounts = {
    orders: number;
    late: number;
    early: number;
    totalProductionHours: number;
    isOverLimit: boolean;
};

export const getCellCounts = (steps: ScheduledStep[], productionHourLimit?: number): CellCounts => {
    const orderStatuses = new Map<string, { hasLate: boolean; hasEarly: boolean }>();
    let totalProductionHours = 0;

    for (const s of steps) {
        const entry = orderStatuses.get(s.orderId);
        if (entry) {
            if (s.scheduleStatus === ScheduleStatus.LATE) entry.hasLate = true;
            if (s.scheduleStatus === ScheduleStatus.EARLY) entry.hasEarly = true;
        } else {
            orderStatuses.set(s.orderId, {
                hasLate: s.scheduleStatus === ScheduleStatus.LATE,
                hasEarly: s.scheduleStatus === ScheduleStatus.EARLY,
            });
        }
        // Sum up production hours for all steps
        if (s.estimatedProductionHours) {
            totalProductionHours += s.estimatedProductionHours;
        }
    }
    let late = 0;
    let early = 0;
    orderStatuses.forEach((entry) => {
        if (entry.hasLate) late++;
        else if (entry.hasEarly) early++;
    });

    const isOverLimit =
        productionHourLimit !== undefined && productionHourLimit > 0 && totalProductionHours > productionHourLimit;

    return { orders: orderStatuses.size, late, early, totalProductionHours, isOverLimit };
};

export const getTableCellColor = (counts: CellCounts, productionHourLimit?: number): string => {
    if (counts.orders === 0) return 'transparent';
    if (counts.isOverLimit) return '#ffcdd2';
    const limit = productionHourLimit && productionHourLimit > 0 ? productionHourLimit : 8;
    const ratio = counts.totalProductionHours / limit;
    if (ratio <= 0.17) return '#f5f5f5';
    if (ratio <= 0.33) return '#eeeeee';
    if (ratio <= 0.5) return '#e0e0e0';
    if (ratio <= 0.67) return '#d6d6d6';
    if (ratio <= 0.83) return '#c8c8c8';
    return '#b8b8b8';
};

// --- Detail utilities ---

export const getDetailCellColor = (steps: ScheduledStep[]): string => {
    if (steps.length === 0) return 'transparent';
    if (steps.some((s) => s.scheduleStatus === ScheduleStatus.LATE)) return '#ffcdd2';
    if (steps.every((s) => s.scheduleStatus === ScheduleStatus.EARLY)) return '#e8f5e9';
    return '#f5f5f5';
};

export const getTooltipContent = (steps: ScheduledStep[]): string => {
    if (steps.length === 0) return '';
    return steps
        .map((step) => {
            const label = statusLabel(step.scheduleStatus);
            const suffix = label ? ` [${label}]` : '';
            return `${step.orderDisplayId}${suffix}`;
        })
        .join('\n');
};

// --- Date helpers ---

/**
 * Timezone the lab's opening hours are authored in.
 *
 * Opening hours are stored as absolute timestamps written in the lab's local
 * wall-clock time. Extracting the hour in this zone — rather than via the viewer's
 * `Date.getHours()` — keeps the dashboard correct regardless of the browser timezone
 * and consistent with the backend scheduler, which reads them the same way.
 */
const LAB_TIME_ZONE = 'Europe/Stockholm';

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Wall-clock hour (0–23) of a timestamp as observed in the lab timezone. */
const getHourInLabZone = (value: number | Date): number => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: LAB_TIME_ZONE,
        hourCycle: 'h23',
        hour: '2-digit',
    }).formatToParts(new Date(value));
    return Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
};

/**
 * Returns the lab's configured opening/closing hour for the weekday of `date`.
 *
 * `close` is exclusive — a lab open 08:00–17:00 has its last working-hour slot at
 * 16:00 (the 16:00–17:00 slot), matching the backend plan-overview convention
 * (`for (let h = open; h < close; h++)`).
 *
 * When the viewed weekday has no active schedule, falls back to the lab's overall
 * active-day span (so a closed day still shows the lab's normal working window rather
 * than an arbitrary default), then to 9–17 only when no day is configured at all.
 */
export const getLabHourRange = (date: Date, openingHours: Hours[] = []): { open: number; close: number } => {
    const daySchedule = openingHours.find((entry) => entry.day === WEEKDAY_NAMES[date.getDay()]);

    if (daySchedule?.active && daySchedule.time) {
        return {
            open: getHourInLabZone(daySchedule.time.from),
            close: getHourInLabZone(daySchedule.time.to),
        };
    }

    let open = 24;
    let close = 0;
    for (const entry of openingHours) {
        if (!entry.active || !entry.time) continue;
        open = Math.min(open, getHourInLabZone(entry.time.from));
        close = Math.max(close, getHourInLabZone(entry.time.to));
    }

    return open < close ? { open, close } : { open: 9, close: 17 };
};

export const getToday = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

// --- Step schedule utilities ---

export const SCHEDULE_COLORS: Record<ScheduleStatus, string> = {
    [ScheduleStatus.LATE]: '#e53935',
    [ScheduleStatus.EARLY]: '#43a047',
    [ScheduleStatus.ON_SCHEDULE]: '#1e88e5',
    [ScheduleStatus.PAUSED]: '#fb8c00',
};

// Match the plan-overview span visuals (plan-overview-dialog.types.ts) for a consistent look.
export const SCHEDULE_BALL_SIZE = 8;
export const SCHEDULE_LINE_HEIGHT = 3;
export const SCHEDULE_ARROW_SIZE = 20;

export const getStartHour = (step: ScheduledStep, date: Date): number => {
    if (!step.startDate) return step.deadline.getHours();
    if (isSameDay(step.startDate, date)) return step.startDate.getHours();
    if (step.startDate < date) return 0;
    return step.deadline.getHours();
};

export const getEndHour = (step: ScheduledStep, date: Date): number => {
    if (isSameDay(step.deadline, date)) return step.deadline.getHours();
    if (step.deadline > date) return 23;
    return step.startDate ? step.startDate.getHours() : 0;
};

export const isSpanFromYesterday = (step: ScheduledStep, date: Date): boolean => {
    return step.startDate != null && !isSameDay(step.startDate, date) && step.startDate < date;
};

export const isSpanToTomorrow = (step: ScheduledStep, date: Date): boolean => {
    return step.deadline != null && !isSameDay(step.deadline, date) && step.deadline > date;
};
